import HistoryIcon from "@/component/Icons/HistoryIcon";
import PrizePoolIcon from "@/component/Icons/PrizePoolIcon";
import ModalShweHoursReward from "@/component/ModalShweHoursReward";
import { ID_TAB_MINI_GAME, IS_UPDATE_VERSION_3_PROJECT, IS_UPDATE_VERSION_3_PROJECT_LOGIN } from "@/helper/const";
import { getLoyaltyPoint } from '@/Redux/futures/loyalty-point/actions';
import { getStreakStatus } from "@/Redux/futures/shweJourney/actions";
import { curIsMaster, curLoginStreakStatus, curLoyaltyPoint, curNotifications, curStateAccount, curStateMiniGame } from "@/Redux/selector";
import homeBgMaster from "@assets/web-v3-images/home-bg-master.png";
import homeBg from "@assets/web-v3-images/home-bg.png";
import PATH from "@config/PATH";
import { mediaQueryPoint, useMediaQuery } from "@hooks";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import FeatureCards from "./components/FeatureCards";
import GuessPriceCard from "./components/GuessPriceCard";
import GuessTicker from "./components/GuessTicker";
import HomeHeader from "./components/HomeHeader";
import LoyaltyWallet from "./components/LoyaltyWallet";
import PrizeShowcase from "./components/PrizeShowcase";
import ShweHoursIcon from "./components/ShweHoursIcon";
import SocialLinks from "./components/SocialLinks";
import WeeklyActionBanner from "./components/WeeklyActionBanner";
import { geNotificationsUnreadCount } from '@/Redux/futures/notification/actions';
import { useAppNavigate } from '@/utils/hooks/useAppNavigate';
import { hasAuthCookie } from '@/utils/auth';
import { useLoginModal } from '@/contexts/LoginModalContext';
import PurchaseSuccess from '@/component/popups/ui-4/PurchaseSuccess';
import ReachedMilestones from '@/component/popups/ui-4/ReachedMilestones';
import FollowPopup from '@/component/popups/ui-3/FollowPopup';
import Treasure from '@/component/popups/ui-2/Treasure';

// Mock data — thay bằng dữ liệu API (React Query) + store khi backend sẵn sàng.
const MOCK_ENDS_AT = new Date(Date.now() + ((2 * 24 + 14) * 60 + 23) * 60_000);

// TODO(tạm): giả lập polling điểm LP để xem animation thanh tiến trình + hộp quà.
// Xoá khi nối API thật (React Query) — LoyaltyWallet chỉ nhận currentLp qua prop.
const LP_MAX = 2000;
const LP_STEP = 180;
const LP_TICK_MS = 1500;

// Map key của từng feature card -> route tương ứng (key khai báo ở FeatureCards)
const CARD_PATHS = {
  journal: PATH.SHWE_JOURNEY,
  vault: PATH.SHWE_VAULT,
  daily: PATH.DAILY_REWARD,
  // Route mini-game cần :id -> vào tab "home" (giống RightWeb/PopupIntro).
  puzzle: `${PATH.MINI_GAME}/${ID_TAB_MINI_GAME.home}`,
};

/**
 * Chuẩn hoá thông tin hồ sơ (KHÔNG gồm số lượt — số lượt lấy từ API riêng bên
 * dưới). Trả về undefined khi chưa có data -> HomeHeader dùng mặc định.
 *
 * TODO(api): backend chưa chốt tên field nên tạm dò vài biến thể hay gặp.
 */
const mapProfile = (data) => {
  if (!data) return undefined;
  return {
    name: data.name ?? data.full_name ?? data.fullName ?? data.username ?? "",
    avatarUrl: data.image ?? "",
  };
};

/**
 * Tổng số lượt còn lại = CỘNG DỒN tất cả các loại lượt trong response remain-turn
 * (vd lượt free + lượt mua + lượt thưởng...), không lấy một field duy nhất.
 *
 * Dò được cả 3 dạng response hay gặp:
 *   - number:  trả thẳng
 *   - array:   [{ turn: 3 }, ...] hoặc [3, 5, ...] -> reduce cộng dồn
 *   - object:  { free: 3, paid: 5, ... } -> reduce cộng mọi giá trị số
 *
 * TODO(api): khi chốt shape thật thì rút gọn lại đúng field của backend.
 * Trả về undefined khi chưa có data -> HomeHeader dùng mặc định.
 */
const numericValue = (v) =>
  typeof v === "number"
    ? v
    : v && typeof v === "object"
      ? (v.turn ?? v.turns ?? v.count ?? v.value ?? v.remain ?? 0)
      : 0;

const sumTurns = (data) => {
  if (data == null) return undefined;
  if (typeof data === "number") return data;

  const buckets = Array.isArray(data) ? data : Object.values(data);
  return buckets.reduce((total, item) => total + numericValue(item), 0);
};

const mapCurrentPrize = (data) => {
  if (!data) return undefined;
  return {
    id: data.product_id ?? null,
    code: data.product_code ?? "",
    cp_id: data.cp_id ?? null,
    name: data.product_name ?? "",
    image: data.product_image ?? "",
    price: data.product_price ?? 0,
    start_price: data.start_price ?? 0,
    start_time: data.start_time ?? null,
    end_time: data.end_time ?? null,
    status: data.status ?? null,
    count_user: data.count_user ?? null,
    create_time: data.create_time ?? null,
    update_time: data.update_time ?? null,
  };
};

/**
 * Thời gian còn lại tới khi kết thúc, rút gọn cho card Daily Reward:
 *   >1 ngày -> "2d 3h", còn giờ -> "3h 14m", dưới 1 giờ -> "14m".
 * Trả "" khi chưa có end_time -> card hiển thị "Ends in" trống.
 */
const formatEndsIn = (endTime) => {
  if (!endTime) return "";
  const diffMs = new Date(endTime).getTime() - Date.now();
  if (diffMs <= 0) return "0m";
  const totalMin = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMin / 1440);
  const hours = Math.floor((totalMin % 1440) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const Home = ({
  currentProduct,
  upNextProduct,
  banner,
  selectedProduct,
  results,
  account,
  bidHistory,
}) => {
  const navigateApp = useAppNavigate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const miniGame = useSelector(curStateMiniGame);
  const notifications = useSelector(curNotifications);
  const { loginStreakStatus } = useSelector(curLoginStreakStatus);
  const { loyaltyPoint } = useSelector(curLoyaltyPoint);

  const { openLogin } = useLoginModal();
  const isAuthed = hasAuthCookie() || Boolean(account?.userInfo) || Boolean(account?.token);

  // Master = cờ toàn cục trong store (suy từ rank login-streak khi fetch).
  const isMaster = useSelector(curIsMaster);
  // Mobile (điện thoại) thì KHÔNG thu nhỏ vừa màn hình — giữ cỡ thật + cuộn.
  const isMobile = useMediaQuery(`(max-width: ${mediaQueryPoint.md}px)`);

  const profile = mapProfile(account?.userInfo);
  const turns = sumTurns(account?.remainTurn);
  const currentPrize = mapCurrentPrize(currentProduct);
  // Chỉ tạo object user khi có ít nhất một nguồn dữ liệu, để lúc chưa có gì thì
  // HomeHeader vẫn nhận undefined và hiển thị giá trị mặc định của nó.
  const user =
    profile || turns !== undefined ? { ...profile, turns } : undefined;

  /* Popup thưởng Shwe Hours. KHÔNG tự bật khi vào Home — chỉ mở từ hai chỗ:
     GuessPriceCard (vừa đoán đủ lượt thứ 10, sau khi modal xác nhận đóng) và
     ShweHoursIcon (bấm icon lúc đã nhận thưởng). Response đặt giá trả về
     {type, pieceImage} còn API đếm lượt trả {reward_type, piece_image} nên
     chuẩn hoá về một dạng ngay tại đây. */
  const [shweHoursReward, setShweHoursReward] = useState(null);
  const showShweHoursReward = (reward) => {
    if (!reward) return;
    setShweHoursReward({
      type: reward.type ?? reward.reward_type,
      pieceImage: reward.pieceImage ?? reward.piece_image,
    });
  };

  // Dữ liệu Shwe Puzzle (mini-game) không được fetch toàn cục -> chủ động lấy
  // khi đã đăng nhập để card có số thật (free coins + số phần thưởng).
  useEffect(() => {
    if (account?.userInfo) {
      // dispatch(getDataSpinUserMiniGame());
      // dispatch(getDataAwardListMiniGame());
      IS_UPDATE_VERSION_3_PROJECT && dispatch(getStreakStatus({}));
      IS_UPDATE_VERSION_3_PROJECT && dispatch(getLoyaltyPoint({}));
      IS_UPDATE_VERSION_3_PROJECT && dispatch(geNotificationsUnreadCount({}));
    }
  }, [account?.userInfo, dispatch]);

  // Số liệu 4 card: daily + puzzle lấy DATA THẬT; journal + vault tạm giữ mock
  // (chưa có nguồn API).
  const stats = {
    // Journal = tiến độ chuỗi đăng nhập: ngày hiện tại / độ dài chu kỳ.
    journal: {
      current: loginStreakStatus?.cycle_day ?? 0,
      total: loginStreakStatus?.cycle_length ?? 0,
    },
    vault: { ready: 0, pieces: 0 },
    daily: { endsIn: formatEndsIn(currentProduct?.end_time) },
    puzzle: {
      freeCoins: miniGame?.dataSpinUserMini?.spin_left_free ?? 0,
      // TODO(xác nhận): chưa có field "số phần thưởng còn lại" riêng -> tạm dùng
      // số loại giải trong pool (dataAwardListMini.length).
      rewardsLeft: miniGame?.dataAwardListMini?.length ?? 0,
    },
  };

  const handleToPackagePage = () => {
    // IS_UPDATE_VERSION_3_PROJECT_LOGIN ? navigateApp(PATH.PACKAGE) : navigate(PATH.PACKAGE)
    if (!IS_UPDATE_VERSION_3_PROJECT_LOGIN) {
      navigate(PATH.PACKAGE);
      return;
    }

    if (isAuthed) {
      navigateApp(PATH.PACKAGE);
    } else {
      openLogin(() => navigateApp(PATH.PACKAGE));
    }
  }

  const handleSelectCard = (key) => {
    const path = CARD_PATHS[key];

    // puzzle không cần login, các card còn lại phải check trước khi vào
    if (key !== "puzzle" && !isAuthed) {
      return openLogin(() => navigate(path));
    }

    navigate(path);
  };


  return (
    <div
      // ref={pageRef}
      // style={
      //   isMobile
      //     ? undefined
      //     : { transform: `scale(${fitScale})`, transformOrigin: "top center" }
      // }
      className={`relative flex flex-col px-4 md:px-8 ${isMobile ? "flex-1" : ""}`}
    >
      <HomeHeader
        user={user}
        isMaster={isMaster}
        onAddTurns={handleToPackagePage}
        hasNotification={notifications?.notificationUnreadCount?.unread_count === 0 ? false : true}
      />
      <div
        style={{ backgroundImage: `url(${isMaster ? homeBgMaster : homeBg})` }}
        className="bg-size-[100%_auto] bg-no-repeat bg-top min-h-screen absolute inset-0 z-1 top-0"
      />
      <div className="relative z-2 flex flex-col gap-6 pt-6">
        <WeeklyActionBanner endsAt={currentProduct?.end_time} />
        <PrizeShowcase prize={currentPrize} />
        {/* product = item thô từ API: ModalConfirmBid cần product_code/product_name */}
        <div className='-mt-12 sm:-mt-0'>
          <GuessPriceCard
            product={currentProduct}
            isMaster={isMaster}
            onShweHoursReward={showShweHoursReward}
          />
        </div>
        <GuessTicker bidHistory={bidHistory} />
        <FeatureCards stats={stats} onSelect={handleSelectCard} />
        {IS_UPDATE_VERSION_3_PROJECT &&
          <LoyaltyWallet
            today_withdraw_amount={loyaltyPoint?.today_withdraw_amount || 0}
            currentLp={loyaltyPoint?.balance || 0}
            milestones={loyaltyPoint?.withdraw_milestones} />
        }
        <div className="flex justify-between absolute translate-y-[-50%] top-[20%] md:top-[25%] left-0 right-0">
          <HistoryIcon onClick={() => IS_UPDATE_VERSION_3_PROJECT_LOGIN ? navigateApp(PATH.HISTORY) : navigate(PATH.HISTORY)} />
          <PrizePoolIcon onClick={() => IS_UPDATE_VERSION_3_PROJECT_LOGIN ? navigateApp(PATH.PRIZE_POOL) : navigate(PATH.PRIZE_POOL)} />
        </div>
        {/* Chừa chỗ cuối để tay cầm SocialLinks (fixed) không đè lên card cuối. */}
        <div className="h-14 shrink-0" />
      </div>
      {/* Icon Shwe Hours: absolute theo chính khung <div relative> này nên chỉ
            nổi trong vùng trang Home (khác SocialLinks — portal ra body). */}
      <ShweHoursIcon onOpenReward={showShweHoursReward} />
      {shweHoursReward ? (
        <ModalShweHoursReward
          open
          onClose={() => setShweHoursReward(null)}
          rewardType={shweHoursReward.type}
          pieceImage={shweHoursReward.pieceImage}
        />
      ) : null}
      <SocialLinks
      // scale={isMobile ? 1 : fitScale}
      />

      {/* Popup chúc mừng lên Master và popup cảnh báo mất chuỗi (kèm luồng hồi
          sinh) đã chuyển vào popup manager: pu10 / pu9 trong popupRegistry.jsx. */}
    </div>
  );
};

export default Home;
